// §3.6 synthesis: fill anything absent, before hashing (§6). Author-supplied files always win.
import type { BuildMeta, MaslIcon } from "./types.ts";
import type { NormalFile } from "./normalize.ts";
import { DEFAULT_ICON_192, DEFAULT_ICON_512 } from "./templates/icons.ts";
import {
  toBytes,
  headInjection,
  injectIntoHtml,
  singleFileIndex,
  directoryIndex,
  imageSlideshow,
  webManifest,
  NEUTRALIZED_SW,
} from "./templates/text.ts";

// Image extensions that trigger the synthesized slideshow when a no-entry drop is images-only (§14).
// Extension-based (deterministic, no byte sniffing); SVG included (can't be magic-byte sniffed).
const IMAGE_EXTENSIONS = new Set([
  "png", "jpg", "jpeg", "gif", "webp", "avif", "bmp", "svg", "svgz",
  "ico", "apng", "heic", "heif", "jxl", "tif", "tiff",
]);

/** True if `path`'s extension is a recognized image type (case-insensitive). */
export function isImagePath(path: string): boolean {
  const dot = path.lastIndexOf(".");
  const slash = path.lastIndexOf("/");
  if (dot <= slash + 1) return false; // no extension, or dotfile with no ext
  return IMAGE_EXTENSIONS.has(path.slice(dot + 1).toLowerCase());
}

const MANIFEST_PATH = "/manifest.webmanifest";
// Author service workers, neutralized so they cannot control the origin (§3.6, §9.3).
// Detection is by conventional root filename (a heuristic — a build does not execute JS).
const SW_PATHS = new Set(["/sw.js", "/service-worker.js", "/serviceworker.js"]);

const decoder = new TextDecoder("utf-8");

export interface SynthesizedTree {
  files: NormalFile[];
  entryPath: string; // always resolved
  iconPaths: string[]; // icon src paths present in `files`, for MASL icon validation
  icons: MaslIcon[]; // MASL `icons` descriptors
}

export function synthesize(input: NormalFile[], entryPath: string | null, meta: BuildMeta): SynthesizedTree {
  const files = new Map<string, Uint8Array>();
  for (const f of input) files.set(f.path, f.bytes);
  const authorPaths = [...files.keys()]; // snapshot before synthesis

  // 1. Neutralize any author service worker (§9.3).
  for (const p of authorPaths) {
    if (SW_PATHS.has(p)) files.set(p, toBytes(NEUTRALIZED_SW));
  }

  // 2. Icons (§3.6). Author icons (via meta.icons) win; otherwise embed the frozen defaults.
  let icons: MaslIcon[];
  if (meta.icons !== undefined && meta.icons.length > 0) {
    icons = meta.icons;
  } else {
    if (!files.has(DEFAULT_ICON_192.path)) files.set(DEFAULT_ICON_192.path, DEFAULT_ICON_192.bytes);
    if (!files.has(DEFAULT_ICON_512.path)) files.set(DEFAULT_ICON_512.path, DEFAULT_ICON_512.bytes);
    icons = [
      { src: DEFAULT_ICON_192.path, sizes: DEFAULT_ICON_192.sizes, type: "image/png" },
      { src: DEFAULT_ICON_512.path, sizes: DEFAULT_ICON_512.sizes, type: "image/png" },
    ];
  }

  // 3. Entry document: synthesize an index if root inference found none (§14).
  let resolvedEntry = entryPath;
  if (resolvedEntry === null) {
    const nonJunk = authorPaths.slice().sort();
    let indexHtml: string;
    if (nonJunk.length > 0 && nonJunk.every(isImagePath)) {
      // Images-only drop → a navigable slideshow (§14 overridable synthesis default).
      indexHtml = imageSlideshow(meta.name, nonJunk);
    } else if (nonJunk.length === 1) {
      indexHtml = singleFileIndex(meta.name, nonJunk[0]!);
    } else {
      indexHtml = directoryIndex(meta.name, nonJunk);
    }
    files.set("/index.html", toBytes(indexHtml));
    resolvedEntry = "/index.html";
  }

  // 4. Inject manifest link, apple meta, and SW registration into the entry HTML (§3.6).
  const entryBytes = files.get(resolvedEntry)!;
  const injected = injectIntoHtml(decoder.decode(entryBytes), headInjection({ name: meta.name, ...(meta.theme_color !== undefined ? { themeColor: meta.theme_color } : {}) }));
  files.set(resolvedEntry, toBytes(injected));

  // 5. Synthesize the web app manifest if the author did not supply one (§3.6).
  if (!files.has(MANIFEST_PATH)) {
    const wm = webManifest({
      name: meta.name,
      ...(meta.short_name !== undefined ? { short_name: meta.short_name } : {}),
      ...(meta.description !== undefined ? { description: meta.description } : {}),
      ...(meta.theme_color !== undefined ? { theme_color: meta.theme_color } : {}),
      ...(meta.background_color !== undefined ? { background_color: meta.background_color } : {}),
      ...(meta.categories !== undefined ? { categories: meta.categories } : {}),
      icons,
    });
    files.set(MANIFEST_PATH, toBytes(wm));
  }

  const out: NormalFile[] = [...files].map(([path, bytes]) => ({ path, bytes }));
  out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));

  return { files: out, entryPath: resolvedEntry, iconPaths: icons.map((i) => i.src), icons };
}
